/* BEATSEEK — "Did we get this right?" confirmation for an ID result. */
import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ConfirmIdButton = ({ track, userId }) => {
  const [count, setCount] = useState(0);
  const [mine, setMine] = useState(false);
  const [busy, setBusy] = useState(false);

  const skip = !track || !track.title || ['not_found', 'error', 'rate_limit'].includes(track.id) || track.outsideScope;

  useEffect(() => {
    if (skip) return;
    const q = new URLSearchParams({ artist: track.artist || '', title: track.title || '' });
    if (userId) q.set('user_id', userId);
    fetch(`${API}/api/track-id/confirmations?${q}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { setCount(d.count || 0); setMine(!!d.confirmed_by_me); } })
      .catch(() => {});
    // eslint-disable-next-line
  }, [track?.title, track?.artist, userId]);

  if (skip) return null;

  const toggle = async () => {
    if (!userId) return toast.error('Log in to confirm an ID');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/track-id/confirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({
          identified_title: track.title, identified_artist: track.artist,
          isrc: track.isrc || null, acr_id: track.acrId || null,
          confirmed: !mine,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not save that');
      setMine(!!d.confirmed_by_me);
      setCount(d.count || 0);
      if (d.locked) {
        toast.success('🏅 This ID is now locked in', {
          description: 'Enough BeatSeekers agree — from now on BEATSEEK names it instantly at 100%, with no outside lookups',
          duration: 6000,
        });
      } else if (d.confirmed_by_me) {
        const left = Math.max(0, (d.lock_threshold || 3) - (d.count || 0));
        toast.success('Thanks — logged as a correct ID', {
          description: left > 0 ? `${left} more confirmation${left === 1 ? '' : 's'} and this ID locks in for everyone` : undefined,
        });
      } else {
        toast.success('Confirmation removed');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <button
        type="button"
        data-testid="confirm-id-button"
        onClick={toggle}
        disabled={busy}
        title={mine ? 'You confirmed this ID — tap to undo' : 'Tell us the engine named this correctly'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, cursor: busy ? 'wait' : 'pointer',
          padding: '3px 11px', borderRadius: 999,
          fontSize: '0.72rem', fontWeight: mine ? 700 : 400, letterSpacing: '0.02em',
          background: mine ? 'rgba(0,255,136,0.1)' : 'transparent',
          border: `1px solid ${mine ? 'rgba(0,255,136,0.45)' : 'rgba(255,255,255,0.14)'}`,
          color: mine ? '#00ff88' : 'rgba(255,255,255,0.5)',
          transition: 'color 0.2s, border-color 0.2s, background-color 0.2s',
        }}
      >
        <Check size={11} />
        {mine ? 'Confirmed ✓' : 'Did we get this right?'}
      </button>
      {count > 0 && (
        <span data-testid="confirm-id-count" style={{ fontSize: '0.66rem', fontWeight: 400, color: 'rgba(0,255,136,0.6)' }}>
          Confirmed correct by {count} BeatSeeker{count === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
};

export default ConfirmIdButton;
